import { z } from "zod";
import { IsoDateTime, jsonColumn } from "./common.ts";

export const SYNC_STATUSES = ["running", "done", "error", "cancelled", "quota_paused"] as const;

/**
 * Order matters — sync walks these in sequence.
 * `artists` remains for historical sync_runs rows only; Spotify artist
 * enrichment is no longer run (see provider jobs later).
 */
export const SYNC_PHASES = [
  "profile",
  "liked",
  "playlists",
  "playlist_items",
  "artists",
  "fts",
] as const;

/** What a Sync button runs — keeps quota burn intentional. */
export const SYNC_SCOPES = ["liked", "library", "full"] as const;

export const SyncStatusSchema = z.enum(SYNC_STATUSES);
export const SyncPhaseSchema = z.enum(SYNC_PHASES);
export const SyncScopeSchema = z.enum(SYNC_SCOPES);
export type SyncStatus = z.infer<typeof SyncStatusSchema>;
export type SyncPhase = z.infer<typeof SyncPhaseSchema>;
export type SyncScope = z.infer<typeof SyncScopeSchema>;

export const SyncCountsSchema = z.object({
  tracks: z.number().int().nonnegative().default(0),
  albums: z.number().int().nonnegative().default(0),
  artists: z.number().int().nonnegative().default(0),
  playlists: z.number().int().nonnegative().default(0),
  liked: z.number().int().nonnegative().default(0),
  playlist_tracks: z.number().int().nonnegative().default(0),
});

export type SyncCounts = z.infer<typeof SyncCountsSchema>;

/** Where to pick up after a restart, so a kill mid-run costs nothing. */
export const SyncCursorSchema = z.object({
  phase: SyncPhaseSchema.optional(),
  /** Includes legacy `"artists"` from older runs that are no longer startable. */
  scope: z.enum(["liked", "library", "artists", "full"]).optional(),
  budget: z.number().int().positive().optional(),
  offset: z.number().int().nonnegative().optional(),
  playlist_id: z.string().optional(),
  playlist_index: z.number().int().nonnegative().optional(),
  artist_index: z.number().int().nonnegative().optional(),
  /** Playlist ids whose items still need fetching (snapshot changed or empty). */
  pending_playlists: z.array(z.string()).optional(),
  /** Totals for the in-app progress bar. */
  liked_total: z.number().int().nonnegative().optional(),
  liked_done: z.number().int().nonnegative().optional(),
  artists_total: z.number().int().nonnegative().optional(),
  artists_done: z.number().int().nonnegative().optional(),
  playlists_listed: z.number().int().nonnegative().optional(),
});

export type SyncCursor = z.infer<typeof SyncCursorSchema>;

export const SyncRunSchema = z.object({
  id: z.number().int(),
  started_at: IsoDateTime,
  finished_at: IsoDateTime.nullable(),
  status: SyncStatusSchema,
  phase: SyncPhaseSchema.nullable(),
  cursor: jsonColumn(SyncCursorSchema).nullable().default(null),
  counts: jsonColumn(SyncCountsSchema).nullable().default(null),
  /** Per-run budget, so a bug cannot burn the daily quota. */
  api_calls: z.number().int().nonnegative(),
  error: z.string().nullable(),
});

export type SyncRun = z.infer<typeof SyncRunSchema>;

export const SyncProgressSchema = z.object({
  scope: SyncScopeSchema,
  phase: SyncPhaseSchema.nullable(),
  label: z.string(),
  detail: z.string(),
  percent: z.number().min(0).max(100),
  api_calls: z.number().int().nonnegative(),
  budget: z.number().int().positive(),
});

export type SyncProgress = z.infer<typeof SyncProgressSchema>;

export const SyncStartSchema = z.object({
  scope: SyncScopeSchema.default("full"),
  budget: z.number().int().positive().max(2000).optional(),
});

export type SyncStart = z.infer<typeof SyncStartSchema>;

export const QuotaStateSchema = z.object({
  ready_at: IsoDateTime.nullable(),
  started_at: IsoDateTime.nullable(),
  total_sec: z.number().int().nullable(),
  reason: z.enum(["RATE_LIMIT", "QUOTA_EXCEEDED"]).nullable(),
  updated_at: IsoDateTime.nullable(),
});

export type QuotaState = z.infer<typeof QuotaStateSchema>;
